import { createUseStyles } from 'react-jss';
import { Spacings } from '@picsart/design-system/foundation/spacings';
import { CornerRadius } from '@picsart/design-system/foundation/cornerRadius';
import { FontSizes } from '@picsart/design-system/foundation/typography';
import { FontWeights } from '@picsart/design-system/foundation/fonts';
import { BackgroundColors } from '@picsart/design-system/foundation/colors';

import { Breakpoints } from '@/lib/breakpoints';
import { HEADER_CONTROL_HEIGHT } from '@/components/HeaderAuthSection/styleTokens';

/**
 * The slot and its placeholder share one stylesheet, deliberately.
 *
 * A second `createUseStyles` rendered on the server shifts every stylesheet
 * index created after it, so the class names react-jss generated on the server
 * no longer matched the client's (`header-logoImg-1-2-6` against
 * `header-logoImg-0-2-6`). React treats that as a failed hydration and throws
 * the entire server-rendered document away — measured: the page's 31 grids
 * disappeared from the DOM and came back ~2s later as client render, so nothing
 * was scrollable until then. Keeping these rules in the sheet the slot already
 * creates adds no sheet and no mismatch.
 */
const useStyles = createUseStyles(
  {
    slot: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      flexShrink: 0,
      minHeight: HEADER_CONTROL_HEIGHT,
      // No reserved width: the controls are right-aligned in the slot, so any
      // width they did not fill opened a gap between them and the search beside
      // the slot — the field on desktop, the icon on phones — which then read as
      // a separate group. The skeleton below, built from the controls' own
      // geometry, is what keeps the row from reflowing when auth lands.
      '&:has([data-testid="header-auth-section"]) [data-testid="header-auth-skeleton"]': {
        display: 'none',
      },
    },
    '@keyframes auth-skeleton': {
      '0%': { opacity: 0.4 },
      '50%': { opacity: 0.7 },
      '100%': { opacity: 0.4 },
    },
    /**
     * One placeholder per control the settled cluster will show — Pricing,
     * Projects, avatar — rather than a single bar standing in for all three.
     *
     * Each is built from the same geometry as the control it stands for
     * (`HEADER_CONTROL_HEIGHT`, `r20` pill or `circle`, the same `s16` padding
     * and `s8` icon gap), and the two pills size themselves from an invisible
     * copy of their own label at the real font. That is what keeps the header
     * from reflowing when auth lands, and it can't drift the way a hardcoded
     * width would when a label changes.
     *
     * They mirror the *signed-in* cluster. Auth hasn't resolved yet, so the
     * shape is a guess either way; a returning visitor is the case where the
     * placeholder is visible long enough to matter.
     */
    skeleton: {
      display: 'flex',
      alignItems: 'center',
      gap: Spacings.s8,
    },
    skeletonPill: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: Spacings.s8,
      height: HEADER_CONTROL_HEIGHT,
      padding: `0 ${Spacings.s16}`,
      borderRadius: CornerRadius.r20,
      backgroundColor: BackgroundColors.tint2,
      flexShrink: 0,
      animation: '$auth-skeleton 1.6s ease-in-out infinite',
      '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
    },
    // Hidden below SM, exactly as the Pricing pill it stands for is.
    skeletonPricing: {
      display: 'none',
      [Breakpoints.SM]: { display: 'inline-flex' },
    },
    // Square and label-less on phones, pill with label from SM up — the Projects
    // button's own responsive shape.
    skeletonProjects: {
      width: HEADER_CONTROL_HEIGHT,
      padding: 0,
      justifyContent: 'center',
      [Breakpoints.SM]: { width: 'auto', padding: `0 ${Spacings.s16}` },
    },
    // Stands in for the 18px DS icon each pill leads with.
    skeletonIcon: {
      width: 18,
      height: 18,
      flexShrink: 0,
    },
    /**
     * An invisible copy of the real label, so the pill is exactly as wide as it
     * will be. `visibility: hidden` and not `opacity`/`color: transparent`: it
     * must keep its box but never be read aloud, and the group is `aria-hidden`
     * at the call site too.
     */
    skeletonLabel: {
      fontSize: FontSizes.t4,
      fontWeight: FontWeights.semibold,
      lineHeight: 1,
      whiteSpace: 'nowrap',
      visibility: 'hidden',
    },
    // Hidden below SM with its label, leaving the square icon-only shape.
    skeletonProjectsLabel: {
      display: 'none',
      [Breakpoints.SM]: { display: 'inline' },
    },
    // The avatar plus its 2px gradient ring — a 36px circle, like `avatarRing`.
    skeletonAvatar: {
      width: HEADER_CONTROL_HEIGHT,
      height: HEADER_CONTROL_HEIGHT,
      borderRadius: CornerRadius.circle,
      backgroundColor: BackgroundColors.tint2,
      flexShrink: 0,
      animation: '$auth-skeleton 1.6s ease-in-out infinite',
      '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
    },
  },
  { name: 'header-auth-slot' },
);

export default useStyles;
